import { useState, useCallback } from 'react';
import { router } from '@inertiajs/react';
import GmLayout from '@/layouts/GmLayout';

/**
 * Hex data for user's hexes.
 */
interface HexData {
    id: number;
    name: string;
}

/**
 * Role data from the backend.
 */
interface RoleData {
    value: string;
    label: string;
}

/**
 * Feature data from the backend.
 */
interface FeatureData {
    key: string;
    name: string;
    description: string;
}

/**
 * User data from the backend.
 */
interface UserData {
    id: number;
    username: string | null;
    name: string;
    email: string | null;
    discord_id: string | null;
    role: RoleData;
    is_banned: boolean;
    hexes_count: number;
    hexes: HexData[];
    features: Record<string, boolean>;
    created_at: string;
}

/**
 * Pagination links from Laravel.
 */
interface PaginationLink {
    url: string | null;
    label: string;
    active: boolean;
}

/**
 * Paginated response from Laravel.
 */
interface PaginatedUsers {
    data: UserData[];
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
    links: PaginationLink[];
}

interface Props {
    users: PaginatedUsers;
    roles: RoleData[];
    features: FeatureData[];
    filters: {
        search: string | null;
        role: string | null;
        banned: string | null;
    };
    // Whether the current user can assign the GM role (only GMs can)
    canAssignGmRole: boolean;
}

/**
 * Format date in a readable format.
 */
function formatDate(isoString: string): string {
    return new Date(isoString).toLocaleDateString();
}

/**
 * GM User Moderation page - displays all users with moderation actions.
 * Moderators can access this page but cannot assign the GM role.
 */
export default function Users({ users, roles, features, filters, canAssignGmRole }: Props) {
    const [search, setSearch] = useState(filters.search || '');
    const [roleFilter, setRoleFilter] = useState(filters.role || '');
    const [bannedFilter, setBannedFilter] = useState(filters.banned || '');

    // Handle search/filter submission
    const handleFilter = useCallback(() => {
        router.get('/gm/users', {
            search: search || undefined,
            role: roleFilter || undefined,
            banned: bannedFilter || undefined,
        }, { preserveState: true });
    }, [search, roleFilter, bannedFilter]);

    // Clear all filters
    const handleClearFilters = () => {
        setSearch('');
        setRoleFilter('');
        setBannedFilter('');
        router.get('/gm/users', {}, { preserveState: true });
    };

    return (
        <GmLayout title="User Moderation" activePath="/gm/users">
            <div className="max-w-7xl">
                <div className="flex items-center justify-between mb-6">
                    <h1 className="text-2xl font-bold text-white">User Moderation</h1>
                    <div className="text-gray-400">
                        {users.total} total user{users.total !== 1 ? 's' : ''}
                    </div>
                </div>

                {/* Search and Filters */}
                <div className="bg-slate-800 border border-slate-700 rounded-lg p-4 mb-6">
                    <div className="flex flex-wrap gap-4 items-end">
                        {/* Search Input */}
                        <div className="flex-1 min-w-[200px]">
                            <label className="block text-sm text-gray-400 mb-1">Search</label>
                            <input
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleFilter()}
                                placeholder="Username, name, email, or Discord ID"
                                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white placeholder-gray-400"
                            />
                        </div>

                        {/* Role Filter */}
                        <div className="w-40">
                            <label className="block text-sm text-gray-400 mb-1">Role</label>
                            <select
                                value={roleFilter}
                                onChange={(e) => setRoleFilter(e.target.value)}
                                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white"
                            >
                                <option value="">All Roles</option>
                                {roles.map((role) => (
                                    <option key={role.value} value={role.value}>
                                        {role.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Banned Filter */}
                        <div className="w-40">
                            <label className="block text-sm text-gray-400 mb-1">Status</label>
                            <select
                                value={bannedFilter}
                                onChange={(e) => setBannedFilter(e.target.value)}
                                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white"
                            >
                                <option value="">All Users</option>
                                <option value="false">Active</option>
                                <option value="true">Banned</option>
                            </select>
                        </div>

                        {/* Filter Buttons */}
                        <div className="flex gap-2">
                            <button
                                onClick={handleFilter}
                                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded transition-colors"
                            >
                                Apply
                            </button>
                            <button
                                onClick={handleClearFilters}
                                className="px-4 py-2 bg-slate-600 hover:bg-slate-500 text-white rounded transition-colors"
                            >
                                Clear
                            </button>
                        </div>
                    </div>
                </div>

                {/* User Table */}
                {users.data.length === 0 ? (
                    <div className="text-center py-12 text-gray-400">
                        No users found.
                    </div>
                ) : (
                    <div className="bg-slate-800 border border-slate-700 rounded-lg overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-slate-700">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">User</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Role</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Status</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Features</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Hexes</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Joined</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-700">
                                    {users.data.map((user) => (
                                        <UserRow key={user.id} user={user} roles={roles} features={features} canAssignGmRole={canAssignGmRole} />
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* Pagination */}
                {users.last_page > 1 && (
                    <Pagination links={users.links} />
                )}
            </div>
        </GmLayout>
    );
}

/**
 * UserRow - Displays a single user row with actions.
 * @param canAssignGmRole - Whether the current user can assign the GM role (only GMs can)
 */
function UserRow({ user, roles, features, canAssignGmRole }: { user: UserData; roles: RoleData[]; features: FeatureData[]; canAssignGmRole: boolean }) {
    const [isChangingRole, setIsChangingRole] = useState(false);
    const [isTogglingBan, setIsTogglingBan] = useState(false);
    const [togglingFeature, setTogglingFeature] = useState<string | null>(null);
    const [showHexes, setShowHexes] = useState(false);

    // Filter roles based on permissions:
    // - If current user cannot assign GM role, hide the GM option from the dropdown
    // - Exception: if the user being viewed is already a GM, show all roles (but the select will be disabled)
    const availableRoles = canAssignGmRole
        ? roles
        : roles.filter((role) => role.value !== 'gm');

    // Determine if role changes should be disabled:
    // - While a role change is in progress
    // - If the target user is a GM and current user is not a GM (moderators can't modify GMs)
    const isRoleChangeDisabled = isChangingRole || (user.role.value === 'gm' && !canAssignGmRole);

    // Handle role change
    const handleRoleChange = (newRole: string) => {
        setIsChangingRole(true);
        router.patch(`/gm/users/${user.id}/role`, { role: newRole }, {
            preserveScroll: true,
            onFinish: () => setIsChangingRole(false),
        });
    };

    // Handle ban toggle
    const handleToggleBan = () => {
        setIsTogglingBan(true);
        router.patch(`/gm/users/${user.id}/ban`, {}, {
            preserveScroll: true,
            onFinish: () => setIsTogglingBan(false),
        });
    };

    // Handle feature toggle (only GMs can do this)
    const handleToggleFeature = (featureKey: string) => {
        setTogglingFeature(featureKey);
        router.patch(`/gm/users/${user.id}/feature`, { feature: featureKey }, {
            preserveScroll: true,
            onFinish: () => setTogglingFeature(null),
        });
    };

    return (
        <tr className={user.is_banned ? 'bg-red-900/20' : ''}>
            {/* User Info */}
            <td className="px-4 py-3">
                <div>
                    <p className="text-white font-medium">{user.username || user.name}</p>
                    <p className="text-xs text-gray-400">{user.email}</p>
                    {user.discord_id && (
                        <p className="text-xs text-gray-500">Discord: {user.discord_id}</p>
                    )}
                    <p className="text-xs text-gray-500">ID: {user.id}</p>
                </div>
            </td>

            {/* Role */}
            <td className="px-4 py-3">
                <select
                    value={user.role.value}
                    onChange={(e) => handleRoleChange(e.target.value)}
                    disabled={isRoleChangeDisabled}
                    className="px-2 py-1 bg-slate-700 border border-slate-600 rounded text-sm text-white disabled:opacity-50"
                    title={user.role.value === 'gm' && !canAssignGmRole ? 'Only Game Masters can modify another Game Master\'s role' : ''}
                >
                    {/* If user is a GM but current user can't assign GM, show the GM option as selected but disabled */}
                    {user.role.value === 'gm' && !canAssignGmRole ? (
                        <option value="gm">{user.role.label}</option>
                    ) : (
                        availableRoles.map((role) => (
                            <option key={role.value} value={role.value}>
                                {role.label}
                            </option>
                        ))
                    )}
                </select>
            </td>

            {/* Status */}
            <td className="px-4 py-3">
                {user.is_banned ? (
                    <span className="px-2 py-1 bg-red-600 text-white text-xs rounded">Banned</span>
                ) : (
                    <span className="px-2 py-1 bg-green-600 text-white text-xs rounded">Active</span>
                )}
            </td>

            {/* Features */}
            <td className="px-4 py-3">
                {features.length === 0 ? (
                    <span className="text-gray-500">-</span>
                ) : (
                    <div className="space-y-1">
                        {features.map((feature) => {
                            const isActive = user.features[feature.key] ?? false;
                            const isToggling = togglingFeature === feature.key;
                            return (
                                <button
                                    key={feature.key}
                                    onClick={() => handleToggleFeature(feature.key)}
                                    disabled={isToggling || !canAssignGmRole}
                                    title={!canAssignGmRole ? 'Only Game Masters can toggle features' : feature.description}
                                    className={`block w-full text-left px-2 py-1 text-xs rounded transition-colors disabled:cursor-not-allowed ${
                                        isActive
                                            ? 'bg-green-600/30 text-green-400 hover:bg-green-600/40'
                                            : 'bg-slate-600/30 text-gray-400 hover:bg-slate-600/50'
                                    } ${isToggling ? 'opacity-50' : ''} ${!canAssignGmRole ? 'opacity-50' : ''}`}
                                >
                                    {isToggling ? '...' : isActive ? '✓' : '✗'} {feature.name}
                                </button>
                            );
                        })}
                    </div>
                )}
            </td>

            {/* Hexes */}
            <td className="px-4 py-3">
                {user.hexes_count === 0 ? (
                    <span className="text-gray-500">None</span>
                ) : (
                    <div>
                        <button
                            onClick={() => setShowHexes(!showHexes)}
                            className="text-purple-400 hover:text-purple-300 text-sm"
                        >
                            {user.hexes_count} hex{user.hexes_count !== 1 ? 'es' : ''}
                        </button>
                        {showHexes && (
                            <div className="mt-1 space-y-1">
                                {user.hexes.map((hex) => (
                                    <a
                                        key={hex.id}
                                        href={`/hex/${hex.id}`}
                                        className="block text-xs text-purple-400 hover:text-purple-300"
                                    >
                                        → {hex.name}
                                    </a>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </td>

            {/* Joined Date */}
            <td className="px-4 py-3 text-sm text-gray-400">
                {formatDate(user.created_at)}
            </td>

            {/* Actions */}
            <td className="px-4 py-3">
                <button
                    onClick={handleToggleBan}
                    disabled={isTogglingBan || user.role.value === 'gm'}
                    className={`px-3 py-1 text-xs rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                        user.is_banned
                            ? 'bg-green-600 hover:bg-green-700 text-white'
                            : 'bg-red-600 hover:bg-red-700 text-white'
                    }`}
                    title={user.role.value === 'gm' ? 'Cannot ban Game Masters' : ''}
                >
                    {isTogglingBan ? '...' : user.is_banned ? 'Unban' : 'Ban'}
                </button>
            </td>
        </tr>
    );
}

/**
 * Pagination - Displays pagination links.
 */
function Pagination({ links }: { links: PaginationLink[] }) {
    return (
        <div className="mt-6 flex justify-center gap-1">
            {links.map((link, index) => (
                <a
                    key={index}
                    href={link.url || '#'}
                    className={`px-3 py-1 rounded text-sm ${
                        link.active
                            ? 'bg-purple-600 text-white'
                            : link.url
                            ? 'bg-slate-700 text-gray-300 hover:bg-slate-600'
                            : 'bg-slate-800 text-gray-500 cursor-not-allowed'
                    }`}
                    dangerouslySetInnerHTML={{ __html: link.label }}
                />
            ))}
        </div>
    );
}

